import React from "react";
import { ArrowRight, ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence, easeOut, easeIn } from "framer-motion";

import slideAnalytics from "@/assets/marketing/stats-leaderboard.jpeg";
import slideBranding from "@/assets/marketing/court-match.jpeg";
import slideQueue from "@/assets/marketing/member-management.jpeg";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useI18n } from "@/contexts/I18nContext";

export default function HomeHeroSlider() {
  const { t, language } = useI18n();
  const isArabic = language === "ar";
  const heroSlides = React.useMemo(
    () =>
      [
        {
          id: "operations",
          image: slideQueue,
          eyebrow: t("landing_hero_member_eyebrow", "Member Management"),
          title: t(
            "landing_hero_member_title",
            "Bring every player, coach, and admin into one organized club system.",
          ),
          description: t(
            "landing_hero_member_desc",
            "Handle onboarding, bulk imports, access control, and first-time login setup from a member workflow designed for modern racket sports clubs.",
          ),
          primaryLabel: t("landing_hero_create_club", "Create your club"),
          primaryTo: "/signup",
          secondaryLabel: t("landing_hero_see_pricing", "See pricing"),
          secondaryTo: "#pricing",
        },
        {
          id: "analytics",
          image: slideAnalytics,
          eyebrow: t("landing_hero_stats_eyebrow", "Stats & Leaderboard"),
          title: t(
            "landing_hero_stats_title",
            "Turn daily club activity into reports, rankings, and usable performance insight.",
          ),
          description: t(
            "landing_hero_stats_desc",
            "Track leaderboard movement, match history, evaluations, daily reporting, and analytics from one clear performance view.",
          ),
          primaryLabel: t("landing_hero_explore_features", "Explore features"),
          primaryTo: "#features",
          secondaryLabel: t("landing_hero_watch_demo", "Watch demo"),
          secondaryTo: "#showcase",
        },
        {
          id: "branding",
          image: slideBranding,
          eyebrow: t("landing_hero_queue_eyebrow", "Queue & Match Control"),
          title: t(
            "landing_hero_queue_title",
            "Run queue flow, court assignment, and match handling from one live surface.",
          ),
          description: t(
            "landing_hero_queue_desc",
            "Keep sessions moving with court assignment modes, live queue visibility, balanced rotations, and clean match completion workflows.",
          ),
          primaryLabel: t("landing_hero_compare_packages", "Compare packages"),
          primaryTo: "#pricing",
          secondaryLabel: t("landing_hero_contact_us", "Contact us"),
          secondaryTo: "#contact",
        },
      ] as const,
    [t],
  );
  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = React.useState(true);
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const slide = heroSlides[activeIndex];

  React.useEffect(() => {
    if (isAutoPlaying) {
      intervalRef.current = setInterval(() => {
        setActiveIndex((current) => (current + 1) % heroSlides.length);
      }, 6000);
    }
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAutoPlaying]);

  React.useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const handlePrev = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsAutoPlaying(false);
    setActiveIndex((current) => (current - 1 + heroSlides.length) % heroSlides.length);
    timeoutRef.current = setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleNext = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsAutoPlaying(false);
    setActiveIndex((current) => (current + 1) % heroSlides.length);
    timeoutRef.current = setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const handleDotClick = (index: number) => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsAutoPlaying(false);
    setActiveIndex(index);
    timeoutRef.current = setTimeout(() => setIsAutoPlaying(true), 10000);
  };

  const toggleAutoPlay = () => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsAutoPlaying(!isAutoPlaying);
  };

  const contentVariants = {
    initial: { opacity: 0, y: 20 },
    animate: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.5, delay: 0.2, ease: easeOut }
    },
    exit: { 
      opacity: 0, 
      y: -20,
      transition: { duration: 0.3, ease: easeIn }
    },
  };

  return (
    <div className="relative h-screen w-full overflow-hidden">
      {/* Background Images - Crossfade */}
      <div className="absolute inset-0">
        {heroSlides.map((item, index) => (
          <div
            key={item.id}
            className="absolute inset-0 h-full w-full transition-opacity duration-700 ease-in-out"
            style={{
              opacity: activeIndex === index ? 1 : 0,
              zIndex: activeIndex === index ? 1 : 0,
            }}
          >
            <img
              src={item.image}
              alt={item.title}
              className={`h-full w-full object-cover ${isArabic ? "-scale-x-100" : ""}`}
            />
          </div>
        ))}
      </div>

      {/* Gradient Overlays */}
      <div
        className={`absolute inset-0 z-10 ${
          isArabic
            ? "bg-gradient-to-l from-black/80 via-black/50 to-transparent"
            : "bg-gradient-to-r from-black/80 via-black/50 to-transparent"
        }`}
      />
      <div className="absolute inset-0 z-10 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
      
      {/* Animated Gradient Blob with new colors */}
      <div className="absolute inset-0 z-10 opacity-40">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.4, 0.6, 0.4],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut",
          }}
          className="absolute top-1/2 left-1/2 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-gradient-to-r from-[#20c5b5]/30 via-[#ef4343]/20 to-transparent blur-3xl"
        />
      </div>

      {/* Content */}
      <div className="relative z-20 flex h-full items-center">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`max-w-2xl lg:max-w-3xl ${isArabic ? "ml-auto text-right" : ""}`}>
            <AnimatePresence mode="wait">
              <motion.div
                key={slide.id}
                variants={contentVariants}
                initial="initial"
                animate="animate"
                exit="exit"
                className="space-y-4 sm:space-y-5 md:space-y-6"
              >
                {/* Eyebrow Badge */}
                <motion.div
                  initial={{ opacity: 0, x: isArabic ? 20 : -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.1, duration: 0.5 }}
                >
                  <Badge className="rounded-full border-[#20c5b5]/40 bg-[#20c5b5]/20 px-3 py-1 text-xs font-medium text-white backdrop-blur-sm shadow-lg sm:px-4 sm:py-1.5 sm:text-sm">
                    {slide.eyebrow}
                  </Badge>
                </motion.div>

                {/* Title - Smaller on mobile */}
                <motion.h3
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                  className="text-3xl font-bold tracking-tight  text-white sm:text-2xl md:text-3xl lg:text-5xl"
                >
                  {slide.title}
                </motion.h3>

                {/* Description */}
                <motion.p
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3, duration: 0.5 }}
                  className="text-sm leading-relaxed text-gray-200 sm:text-base md:text-lg lg:text-xl"
                >
                  {slide.description}
                </motion.p>

                {/* Buttons */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.5 }}
                  className={`flex flex-col gap-3 sm:gap-4 ${isArabic ? "sm:flex-row-reverse" : "sm:flex-row"}`}
                >
                  {slide.primaryTo.startsWith("/") ? (
                    <Button 
                      asChild 
                      size="lg" 
                      className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#20c5b5] to-[#ef4343] px-6 py-5 text-sm font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:px-8 sm:text-base"
                    >
                      <Link to={slide.primaryTo}>
                        <span className={`relative z-10 flex items-center ${isArabic ? "flex-row-reverse" : ""}`}>
                          {slide.primaryLabel}
                          <ArrowRight className={`h-4 w-4 transition-transform duration-300 sm:h-5 sm:w-5 ${isArabic ? "mr-2 rotate-180 group-hover:-translate-x-1" : "ml-2 group-hover:translate-x-1"}`} />
                        </span>
                      </Link>
                    </Button>
                  ) : (
                    <Button 
                      asChild 
                      size="lg" 
                      className="group relative overflow-hidden rounded-full bg-gradient-to-r from-[#20c5b5] to-[#ef4343] px-6 py-5 text-sm font-semibold text-white shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl sm:px-8 sm:text-base"
                    >
                      <a href={slide.primaryTo}>
                        <span className={`relative z-10 flex items-center ${isArabic ? "flex-row-reverse" : ""}`}>
                          {slide.primaryLabel}
                          <ArrowRight className={`h-4 w-4 transition-transform duration-300 sm:h-5 sm:w-5 ${isArabic ? "mr-2 rotate-180 group-hover:-translate-x-1" : "ml-2 group-hover:translate-x-1"}`} />
                        </span>
                      </a>
                    </Button>
                  )}
                  <Button 
                    asChild 
                    variant="outline" 
                    size="lg" 
                    className="rounded-full border-white/30 bg-white/10 px-6 py-5 text-sm font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:scale-105 hover:bg-white/20 hover:text-white sm:px-8 sm:text-base"
                  >
                    <a href={slide.secondaryTo}>{slide.secondaryLabel}</a>
                  </Button>
                </motion.div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="absolute bottom-20 left-0 right-0 z-20 sm:bottom-24">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-1.5 sm:gap-2">
            {heroSlides.map((item, index) => (
              <button
                key={item.id}
                onClick={() => handleDotClick(index)}
                className="group relative flex-1 cursor-pointer"
                aria-label={t("landing_hero_go_to_slide", "Go to slide")} 
              >
                <div className="h-1 overflow-hidden rounded-full bg-white/30 transition-all duration-300 group-hover:bg-white/50">
                  <motion.div
                    className="h-full rounded-full bg-gradient-to-r from-[#20c5b5] to-[#ef4343]"
                    initial={{ width: "0%" }}
                    animate={{
                      width: index === activeIndex ? "100%" : "0%",
                    }}
                    transition={{
                      duration: index === activeIndex && isAutoPlaying ? 6 : 0.3,
                      ease: "linear",
                    }}
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="absolute bottom-20 right-4 z-20 flex gap-2 sm:bottom-24 sm:right-8">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handlePrev}
          className="rounded-full bg-black/40 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/60 hover:shadow-lg sm:p-3"
          aria-label={t("landing_hero_prev_slide", "Previous slide")}
        >
          <ChevronLeft className="h-4 w-4 sm:h-5 sm:w-5" />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={handleNext}
          className="rounded-full bg-black/40 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/60 hover:shadow-lg sm:p-3"
          aria-label={t("landing_hero_next_slide", "Next slide")}
        >
          <ChevronRight className="h-4 w-4 sm:h-5 sm:w-5" />
        </motion.button>
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={toggleAutoPlay}
          className="rounded-full bg-black/40 p-2 text-white backdrop-blur-sm transition-all duration-300 hover:bg-black/60 hover:shadow-lg sm:p-3"
          aria-label={
            isAutoPlaying
              ? t("landing_hero_pause", "Pause slideshow")
              : t("landing_hero_play", "Play slideshow")
          }
        >
          {isAutoPlaying ? <Pause className="h-4 w-4 sm:h-5 sm:w-5" /> : <Play className="h-4 w-4 sm:h-5 sm:w-5" />}
        </motion.button>
      </div>
    </div>
  );
}
